import { Card } from '@/propertyai/components/ui/card';
import { Progress } from '@/propertyai/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/propertyai/components/ui/tabs';
import { TrendingUp, TrendingDown, Info } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export const AVMWidget = ({ valuation, priceHistory }) => {
  if (!valuation) return null;

  const { estimated_value, range, accuracy_percentile, confidence, value_drivers } = valuation;

  // Prepare chart data from price history
  const chartData = (priceHistory || []).map(h => ({
    date: h.date?.substring(0, 7) || '',
    value: h.price,
  }));

  // Add current estimate
  if (chartData.length > 0) {
    chartData.push({ date: 'Now', value: estimated_value });
  }

  const confidenceColor = confidence === 'high' ? 'text-green-600' : confidence === 'medium' ? 'text-amber-600' : 'text-red-600';

  return (
    <Card className="rounded-2xl border border-border bg-background p-5" data-testid="avm-widget">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">PropertyAI Estimate</h3>
        <div className={`flex items-center gap-1 text-xs font-medium ${confidenceColor}`}>
          {confidence === 'high' ? <TrendingUp className="w-3.5 h-3.5" /> : <Info className="w-3.5 h-3.5" />}
          {confidence} confidence
        </div>
      </div>

      {/* Value */}
      <div className="mb-3">
        <div className="text-3xl font-semibold tabular-nums tracking-tight font-['Space_Grotesk']" data-testid="avm-estimated-value">
          ${estimated_value?.toLocaleString()}
        </div>
        <div className="text-sm text-muted-foreground mt-1" data-testid="avm-estimated-range">
          Range: ${range?.low?.toLocaleString()} - ${range?.high?.toLocaleString()}
        </div>
      </div>

      {/* Confidence meter */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-muted-foreground">Accuracy Score</span>
          <span className="text-xs font-medium tabular-nums">{accuracy_percentile}%</span>
        </div>
        <Progress value={accuracy_percentile} className="h-2" data-testid="avm-confidence-meter" />
      </div>

      <Tabs defaultValue="chart" className="w-full">
        <TabsList className="w-full">
          <TabsTrigger value="chart" className="flex-1 text-xs">Trend</TabsTrigger>
          <TabsTrigger value="drivers" className="flex-1 text-xs">Drivers</TabsTrigger>
        </TabsList>

        <TabsContent value="chart" className="mt-3">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={140}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="avmGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(186, 72%, 33%)" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="hsl(186, 72%, 33%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}K`} />
                <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, 'Value']} />
                <Area type="monotone" dataKey="value" stroke="hsl(186, 72%, 33%)" fill="url(#avmGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">No historical data available</p>
          )}
        </TabsContent>

        <TabsContent value="drivers" className="mt-3">
          <div className="space-y-2">
            {value_drivers?.map((driver, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{driver.factor}</span>
                <span className={`font-medium tabular-nums ${
                  driver.impact.startsWith('+') ? 'text-green-600' : 'text-red-600'
                }`}>
                  {driver.impact}
                </span>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </Card>
  );
};

export default AVMWidget;

